/**
 * 🆕 [Phase 35] Dialog centralisé « Normes de matériel ».
 *
 * Workflow inversé : créer/éditer la norme depuis cette page, choisir les
 * unités cibles via un multi-select. La liste d'articles vient de
 * `/api/unites/materiel-disponible-pour-normes` (matériel présent dans les
 * unités, pas l'inventaire général).
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL || '';

export function NormesMaterielDialog({ open, onClose, unitesFromParent = null, focusUniteId = null }) {
  const [normes, setNormes] = useState([]);
  const [articles, setArticles] = useState([]);
  const [unites, setUnites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null = liste, {} = create, norme = edit

  const reload = async () => {
    setLoading(true);
    try {
      const [rN, rA, rU] = await Promise.all([
        axios.get(`${API}/api/normes-materiel`),
        axios.get(`${API}/api/unites/materiel-disponible-pour-normes`),
        unitesFromParent
          ? Promise.resolve({ data: { vehicules: unitesFromParent } })
          : axios.get(`${API}/api/vehicules`),
      ]);
      setNormes(rN.data?.normes || []);
      setArticles(rA.data?.articles || []);
      setUnites(rU.data?.vehicules || rU.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur de chargement des normes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) reload(); /* eslint-disable-next-line */ }, [open]);

  const articleNameById = useMemo(() => {
    const m = {};
    for (const a of articles) m[a.id] = a.nom;
    return m;
  }, [articles]);
  const uniteNameById = useMemo(() => {
    const m = {};
    for (const u of unites) m[u.id] = `${u.numero_unite || ''} ${u.nom || ''}`.trim() || u.id;
    return m;
  }, [unites]);

  const handleDelete = async (n) => {
    if (!window.confirm(`Supprimer la norme « ${n.nom || articleNameById[n.article_id] || 'sans nom'} » ?`)) return;
    try {
      await axios.delete(`${API}/api/normes-materiel/${n.id}`);
      toast.success("Norme supprimée.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="normes-materiel-dialog">
        <DialogHeader>
          <DialogTitle>📋 Normes de matériel</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          {editor !== null ? (
            <NormeEditor
              norme={editor.id ? editor : null}
              articles={articles}
              unites={unites}
              focusUniteId={focusUniteId}
              onCancel={() => setEditor(null)}
              onSaved={() => { setEditor(null); reload(); }}
            />
          ) : (
            <>
              <div className="flex justify-between mb-3">
                <p className="text-xs text-slate-500">{loading ? 'Chargement…' : `${normes.length} norme(s)`}</p>
                <button
                  onClick={() => setEditor({})}
                  className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium"
                  data-testid="btn-add-norme-top-level"
                >
                  + Ajouter une norme
                </button>
              </div>
              {normes.length === 0 ? (
                <p className="text-sm italic text-slate-500 text-center py-6" data-testid="normes-top-level-empty">
                  Aucune norme définie.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {normes.map(n => (
                    <li
                      key={n.id}
                      className="flex items-center gap-2 p-2 border rounded text-sm"
                      data-testid={`normes-top-level-item-${n.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{n.nom || articleNameById[n.article_id] || 'Norme'}</p>
                        <p className="text-[11px] text-slate-500">
                          Qté <strong>{n.quantite}</strong> · {(n.unite_ids || []).length} unité(s) :{' '}
                          {(n.unite_ids || []).slice(0, 4).map(uid => uniteNameById[uid] || uid).join(', ')}
                          {(n.unite_ids || []).length > 4 && ` +${n.unite_ids.length - 4}`}
                        </p>
                      </div>
                      <button
                        onClick={() => setEditor(n)}
                        className="text-xs px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded border border-blue-200"
                        data-testid={`btn-edit-norme-${n.id}`}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(n)}
                        className="text-xs px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded border border-red-200"
                        data-testid={`btn-delete-norme-${n.id}`}
                      >
                        🗑️
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NormeEditor({ norme, articles, unites, focusUniteId, onCancel, onSaved }) {
  const isEdit = !!norme;
  const [articleId, setArticleId] = useState(norme?.article_id || '');
  const [articleSearch, setArticleSearch] = useState('');
  const [quantite, setQuantite] = useState(norme?.quantite ?? 1);
  const [nom, setNom] = useState(norme?.nom || '');
  const [uniteIds, setUniteIds] = useState(norme?.unite_ids || (focusUniteId ? [focusUniteId] : []));
  const [saving, setSaving] = useState(false);

  const filteredArticles = articleSearch.trim()
    ? articles.filter(a => (a.nom || '').toLowerCase().includes(articleSearch.toLowerCase()))
    : articles;

  const toggleUnite = (uid) => {
    setUniteIds(p => p.includes(uid) ? p.filter(x => x !== uid) : [...p, uid]);
  };

  const handleSave = async () => {
    if (!articleId) { toast.error('Sélectionnez un article.'); return; }
    if (!quantite || Number(quantite) <= 0) { toast.error('Quantité invalide.'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/api/normes-materiel/${norme.id}`, {
          quantite: Number(quantite),
          nom: nom || null,
          unite_ids: uniteIds,
        });
        toast.success('Norme modifiée.');
      } else {
        await axios.post(`${API}/api/normes-materiel`, {
          article_id: articleId,
          quantite: Number(quantite),
          nom: nom || null,
          unite_ids: uniteIds,
        });
        toast.success('Norme créée.');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 text-sm" data-testid="norme-editor">
      <div>
        <label className="text-xs font-medium">Article *</label>
        {isEdit ? (
          <p className="px-2 py-1 bg-slate-50 rounded text-sm">{articles.find(a => a.id === articleId)?.nom || articleId}</p>
        ) : (
          <>
            <input
              type="text"
              value={articleSearch}
              onChange={(e) => setArticleSearch(e.target.value)}
              placeholder="Rechercher dans le matériel des unités…"
              className="w-full px-2 py-1.5 border rounded text-sm"
              data-testid="input-search-article-top"
            />
            <div className="mt-1 max-h-32 overflow-y-auto border rounded">
              {filteredArticles.length === 0 ? (
                <p className="p-2 text-xs italic text-slate-400">Aucun article trouvé.</p>
              ) : filteredArticles.map(a => (
                <button
                  type="button"
                  key={a.id}
                  onClick={() => { setArticleId(a.id); setArticleSearch(a.nom); }}
                  className={`block w-full text-left px-2 py-1 text-xs hover:bg-blue-50 ${articleId === a.id ? 'bg-blue-100 font-medium' : ''}`}
                  data-testid={`option-article-top-${a.id}`}
                >
                  {a.nom} <span className="text-[10px] text-slate-400">({a.nb_unites} unité(s))</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      <div>
        <label className="text-xs font-medium">Quantité *</label>
        <input
          type="number" min="0.01" step="0.01"
          value={quantite}
          onChange={(e) => setQuantite(e.target.value)}
          className="w-full px-2 py-1.5 border rounded text-sm"
          data-testid="input-quantite-top"
        />
      </div>
      <div>
        <label className="text-xs font-medium">Nom (optionnel)</label>
        <input
          type="text"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          placeholder="Par défaut : nom de l'article"
          className="w-full px-2 py-1.5 border rounded text-sm"
          data-testid="input-nom-top"
        />
      </div>
      <div>
        <label className="text-xs font-medium">Unités cibles ({uniteIds.length})</label>
        <div className="max-h-40 overflow-y-auto border rounded p-2 space-y-1" data-testid="unites-multi-select">
          {unites.map(u => (
            <label key={u.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 p-1 rounded">
              <input
                type="checkbox"
                checked={uniteIds.includes(u.id)}
                onChange={() => toggleUnite(u.id)}
                data-testid={`check-unite-${u.id}`}
              />
              <span><strong>{u.numero_unite}</strong> {u.nom}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t">
        <button onClick={onCancel} className="px-3 py-1.5 text-xs bg-white border rounded hover:bg-slate-50">Annuler</button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
          data-testid="btn-save-norme-top"
        >
          {saving ? 'Enregistrement…' : (isEdit ? 'Modifier' : 'Enregistrer')}
        </button>
      </div>
    </div>
  );
}

export default NormesMaterielDialog;
